import {
  createContext,
  FunctionComponent,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  Fragment,
} from 'react';
import {
  // eslint-disable-next-line @typescript-eslint/no-restricted-imports
  useUsers as useUsersAlloy,
  UsersContextValue,
  useClientSideDataModification,
  useCurrentToolId,
  TOOL_IDS,
  useDeletedUsers,
  User,
} from '@adsk/alloy-provider';
import { useContextWithValidation } from '@adsk/alloy-react-context';
import { useGetUsersMe } from '../hooks/queries';
import { setPendingUsersNameAsEmail } from '../helpers/userHelpers';
import { Optional } from '../types';
import { EMPTY_ARRAY } from '../constants';

type RFIsUserContextValue = Optional<UsersContextValue, 'removeUserByAutodeskId'>;

export const RFIsUserContext = createContext<RFIsUserContextValue>({
  data: EMPTY_ARRAY,
  loading: true,
} as unknown as RFIsUserContextValue);

const useShouldUseCustomProvider = () => {
  const currentToolId = useCurrentToolId();
  const isRFIsTool = currentToolId === TOOL_IDS.RFIS;
  return isRFIsTool;
};

export const useUsers = () => {
  const shouldUseCustomProvider = useShouldUseCustomProvider();
  if (!shouldUseCustomProvider) {
    // useUsersAlloy creates new object each time.
    const {
      data: alloyActiveUsers = EMPTY_ARRAY,
      loading: alloyActiveUsersLoading,
      error,
      retry,
      addUsers,
      updateUser,
      removeUserById,
    } = useUsersAlloy();

    const { data: deletedUsers = EMPTY_ARRAY, loading: deletedUsersLoading } = useDeletedUsers();
    return useMemo(() => {
      const getUsersWithAutodeskId = () =>
        [...alloyActiveUsers, ...deletedUsers].filter(({ autodeskId }) => !!autodeskId);

      const loading = alloyActiveUsersLoading || deletedUsersLoading;

      return {
        data: loading ? EMPTY_ARRAY : getUsersWithAutodeskId(),
        loading,
        error,
        retry,
        addUsers,
        updateUser,
        removeUserById,
      };
    }, [
      alloyActiveUsers,
      alloyActiveUsersLoading,
      deletedUsers,
      deletedUsersLoading,
      error,
      retry,
      addUsers,
      updateUser,
      removeUserById,
    ]);
  }

  return useContextWithValidation(RFIsUserContext, {
    hook: 'useUser',
    provider: 'RFIsUsersProvider',
  });
};

export const RFIsUsersProvider: FunctionComponent<ReactNode> = ({ children }) => {
  const shouldUseCustomProvider = useShouldUseCustomProvider();
  if (!shouldUseCustomProvider) {
    return <Fragment>{children}</Fragment>;
  }
  const alloyUsers = useUsersAlloy();
  const deletedUsers = useDeletedUsers();
  const usersMe = useGetUsersMe();
  const shouldAddExternalAndDeletedUsers = useRef(true);

  const {
    modifyData,
    addData: addUsers,
    updateEntity: updateUser,
    removeEntityById: removeUserById,
  } = useClientSideDataModification<User>(/** Session? */);

  const loading = alloyUsers.loading || deletedUsers.loading || !usersMe.isSuccess;

  const alloyUsersDataWithAutodeskId = useMemo(
    () => alloyUsers.data?.filter(({ autodeskId }) => !!autodeskId) || alloyUsers.data,
    [alloyUsers.data],
  );

  // Add alloy's SWR cached users to our context
  const data = useMemo(
    () => modifyData(alloyUsersDataWithAutodeskId as User[]) || EMPTY_ARRAY,
    [modifyData, alloyUsersDataWithAutodeskId],
  );

  // Handle external users
  useEffect(() => {
    const canAddExternalAndDeletedUsers =
      shouldAddExternalAndDeletedUsers.current && !loading && usersMe.data;

    if (canAddExternalAndDeletedUsers) {
      const deletedUsersWithAutodeskIds =
        deletedUsers.data?.filter(({ autodeskId }) => !!autodeskId) || EMPTY_ARRAY;

      const existingIds = new Set<string>();
      alloyUsersDataWithAutodeskId.forEach((u) => {
        existingIds.add(u.autodeskId);
      });

      deletedUsersWithAutodeskIds.forEach((u) => {
        existingIds.add(u.autodeskId);
      });

      // if external users are already appear as standard users, we don't want to add them again
      const filteredExternalUsers = usersMe.data.externalUsers.filter(
        ({ autodeskId }) => !!autodeskId && !existingIds.has(autodeskId),
      );

      const externalUsers = setPendingUsersNameAsEmail(filteredExternalUsers);

      addUsers([...externalUsers, ...deletedUsersWithAutodeskIds] as User[]);
      shouldAddExternalAndDeletedUsers.current = false;
    }
  }, [usersMe.data, alloyUsersDataWithAutodeskId, addUsers, loading, deletedUsers.data]);

  const { retry, error } = alloyUsers;
  const value = useMemo(
    () => ({
      data,
      loading,
      error,
      retry,
      addUsers,
      updateUser,
      removeUserById,
    }),
    [addUsers, updateUser, data, error, loading, removeUserById, retry],
  );

  return <RFIsUserContext.Provider value={value}>{children}</RFIsUserContext.Provider>;
};

export default RFIsUsersProvider;
